import React from 'react';
import { useTranslation } from 'react-i18next';

interface GovernanceProps {
  onPartnerClick: () => void;
}

const INDUSTRIES = ['law', 'government', 'healthcare', 'banking', 'insurance', 'education', 'callCenters'] as const;

/** Data-governance panel with industry chips and the closing CTA. */
const Governance: React.FC<GovernanceProps> = ({ onPartnerClick }) => {
  const { t } = useTranslation();

  return (
    <section id="lab-governance" className="relative z-10 px-4 pb-[110px] pt-10 md:px-[72px]">
      <div className="lab-glass mx-auto max-w-[880px] rounded-2xl p-8 text-center md:p-[52px_60px]">
        <div className="font-plexmono text-[11px] tracking-[0.18em] text-lab-asr">
          {t('lab.governance.kicker')}
        </div>
        <h2 className="mb-3.5 mt-4 text-[clamp(26px,5vw,38px)] font-semibold leading-[1.15] tracking-[-0.02em] [text-wrap:balance]">
          {t('lab.governance.title')}
        </h2>
        <p className="mx-auto max-w-[640px] text-[15px] leading-[1.75] text-lab-frost/70 md:text-[15.5px]">
          {t('lab.governance.desc')}
        </p>
        {/* Urgency pillars: data stays home / no GPU bills / yours alone */}
        <div className="mt-8 grid grid-cols-1 gap-3 text-start md:grid-cols-3">
          {(['data', 'cost', 'yours'] as const).map((pillar) => (
            <div key={pillar} className="lab-siri rounded-xl border border-lab-frost/[0.14] bg-white/[0.03] p-5">
              <div className="text-[15px] font-semibold">{t(`lab.governance.pillars.${pillar}.title`)}</div>
              <p className="mt-1.5 text-[13.5px] leading-[1.6] text-lab-frost/65">
                {t(`lab.governance.pillars.${pillar}.desc`)}
              </p>
            </div>
          ))}
        </div>
        <div className="mb-[34px] mt-[30px] flex flex-wrap justify-center gap-2.5">
          {INDUSTRIES.map((industry) => (
            <div
              key={industry}
              className="lab-siri rounded-full border border-lab-frost/30 px-4 py-2 font-plexmono text-xs text-lab-frost/85"
            >
              {t(`lab.governance.industries.${industry}`)}
            </div>
          ))}
          <div className="lab-siri rounded-full border border-dashed border-lab-frost/35 px-4 py-2 font-plexmono text-xs text-lab-frost/60">
            {t('lab.governance.industries.more')}
          </div>
        </div>
        <button
          onClick={onPartnerClick}
          className="lab-siri inline-block rounded-full bg-lab-frost px-[34px] py-[15px] text-[15px] font-semibold text-lab-ink transition-transform hover:-translate-y-0.5"
        >
          {t('lab.governance.cta')}
        </button>
      </div>
    </section>
  );
};

export default Governance;
